import type { VisualThemeId } from './visualThemeTypes';

/** Flat color tokens for ChatListScreen (rebuilt StyleSheet per theme). */
export type ChatListPalette = {
  screenRoot: string;
  aliveDot: string;
  scrim: string;
  chatFilterChip: string;
  chatFilterChipBorder: string;
  chatFilterChipActive: string;
  chatFilterChipActiveBorder: string;
  chatFilterChipText: string;
  chatFilterChipTextActive: string;
  chatSortBtnText: string;
  headerActionBtn: string;
  headerActionBtnBorder: string;
  headerIconTint: string;
  h1: string;
  h2: string;
  smallBtn: string;
  smallBtnBorder: string;
  smallBtnText: string;
  /**
   * Подсветка кнопки «моя дружина», когда есть входящая заявка. Была вшита в ChatListScreen
   * инлайновым янтарём мимо палитры — единственный цвет экрана, не зависящий от темы: на
   * `sun` (янтарный фон, кремовая кнопка) он не читался вовсе.
   */
  smallBtnAlert: string;
  smallBtnAlertBorder: string;
  channelIconStroke: string;
  druzhinaPanel: string;
  druzhinaPanelBorder: string;
  druzhinaTitle: string;
  druzhinaAdd: string;
  druzhinaFilterChip: string;
  druzhinaFilterChipBorder: string;
  druzhinaFilterChipActive: string;
  druzhinaFilterChipActiveBorder: string;
  druzhinaFilterChipText: string;
  druzhinaFilterChipTextActive: string;
  druzhinaHint: string;
  druzhinaEmpty: string;
  druzhinaRow: string;
  druzhinaRowBorder: string;
  druzhinaName: string;
  /** Tint for send/call glyphs on druzhina round actions (must contrast with actionIcon* fills). */
  druzhinaActionIconTint: string;
  /** Dark text-shadow under list titles helps on photo wallpapers; disable on light neutral UI. */
  headerTitleShadow: boolean;
  /**
   * Чем карточка списка отделяется от фона.
   *
   * <p>Раньше — всегда рамкой в 1 px: у карточки чата, у панели дружины, у каждой кнопки. На
   * одном экране набиралось 26 таких обводок, и список читался как таблица, а не как лента.
   * Отделять поверхность принято тоном и тенью, а рамку беречь для интерактивного элемента.
   *
   * <p>Но тень работает только там, где есть куда её отбрасывать: на `#09090b` и `#020617`
   * чёрное по чёрному не видно вовсе, и карточка без рамки слилась бы с фоном. Поэтому выбор
   * зависит от палитры, а не от вкуса: светлые темы — `shadow`, тёмные — `hairline`
   * (та же рамка, но в полпикселя вместо целого: она отделяет, не рисуя таблицу).
   */
  itemSeparation: 'shadow' | 'hairline';
  /**
   * Заливка блока-заглушки внутри карточки, пока список едет по сети.
   *
   * <p>Тон, а не цвет: заглушка обязана читаться как «здесь будет текст», а не как элемент
   * интерфейса. Компонент `Skeleton` сам пульсирует прозрачностью в диапазоне 0.35–0.75,
   * поэтому здесь задаётся уже приглушённое значение — на пике оно не должно спорить
   * с настоящими подписями соседних, уже загруженных строк.
   */
  skeletonBlock: string;
  /**
   * Плавающая панель шапки: список проезжает под ней, поэтому она полупрозрачная.
   *
   * Пара значений, а не одно: `bar` — заливка (она же единственный фон там, где нативного
   * размытия нет), `barTint` — сторона материала для `expo-blur`. Светлое стекло на тёмном
   * фоне даёт молочную муть, тёмное на светлом — грязь, поэтому выбирает палитра.
   */
  bar: string;
  barTint: 'light' | 'dark';
  /**
   * Узор на панели шапки: какой тайл повторять и как громко.
   *
   * <p>Рисунка два. `mail` — почта (конверт, распечатанное письмо, лист, перо): тот же
   * сюжет, что у логотипа, где ворон несёт письмо. `varangian` — дубовые листья, жёлуди,
   * снежинки и вегвизир: он идёт только по ночному лесу варяжской темы, где северный
   * орнамент читается как часть картинки, а не как чужая наклейка.
   *
   * <p>Цвет узора отдельным токеном, а не производным от `headerIconTint`: иконки шапки
   * обязаны быть контрастными, узор — наоборот, обязан оставаться фоном. На варяжской теме
   * они и вовсе разного тона: иконки холодные (`#e2e8f0`), узор тёплый — под костёр.
   *
   * <p>Прозрачность тоже палитровая: на светлых темах хватает 10–12 %, на тёмных тот же
   * узор при этой доле пропадает вовсе, а поверх фотографии леса его, наоборот, приходится
   * выводить громче остальных.
   */
  headerPattern: 'mail' | 'varangian';
  headerPatternTint: string;
  headerPatternOpacity: number;
  actionIcon: string;
  actionIconBorder: string;
  actionIconChat: string;
  actionIconChatBorder: string;
  actionIconCall: string;
  actionIconCallBorder: string;
  actionIconVideo: string;
  actionIconVideoBorder: string;
  requestTitle: string;
  requestRow: string;
  requestRowBorder: string;
  requestName: string;
  requestApprove: string;
  requestReject: string;
  requestPending: string;
  contactRemoveSwipe: string;
  contactRemoveSwipeBorder: string;
  contactRemoveSwipeText: string;
  item: string;
  itemBorder: string;
  itemAccentDirect: string;
  itemAccentGroup: string;
  itemAccentChannel: string;
  /** «Избранное» (эпик 8): четвёртый тип чата и четвёртый акцент карточки. */
  itemAccentSaved: string;
  itemTitle: string;
  presenceDotOnline: string;
  presenceDotOnlineBorder: string;
  presenceDotOffline: string;
  presenceDotOfflineBorder: string;
  groupOnlineBadge: string;
  groupOnlineBadgeBorder: string;
  groupOnlineBadgeText: string;
  unreadBadge: string;
  unreadBadgeBorder: string;
  itemMeta: string;
  itemPreview: string;
  deleteBtn: string;
  deleteBtnBorder: string;
  notifyBtn: string;
  notifyBtnBorder: string;
  notifyMutedSlash: string;
  empty: string;
  modalBg: string;
  modalCard: string;
  settingsSectionTitle: string;
  settingsFieldLabel: string;
  settingsInlineHint: string;
  settingsSectionDivider: string;
  bossRegenBtn: string;
  bossRegenBtnText: string;
  bossRow: string;
  bossRowBorder: string;
  bossRowTitle: string;
  bossRowMeta: string;
  bossDelete: string;
  dmRow: string;
  dmRowBorder: string;
  dmRowName: string;
  dmRowHint: string;
  dmEmpty: string;
  channelDirRow: string;
  channelDirRowBorder: string;
  channelDirDescription: string;
  channelDirMeta: string;
  gSelectedLabel: string;
  gSelectedRow: string;
  gSelectedRowBorder: string;
  gSelectedName: string;
  gRemove: string;
  modalTitle: string;
  input: string;
  inputBorder: string;
  inputText: string;
  hint: string;
  modalBtn: string;
  modalPrimary: string;
  modalDanger: string;
  /**
   * Подпись разрушающего действия ВТОРОГО плана — «удалить фото», «отписаться»: такая кнопка
   * не залита, и красный лежит текстом на нейтральном `modalBtn`.
   *
   * <p>Отдельный токен, а не переиспользованный `modalDanger`: тот подобран как ЗАЛИВКА под
   * белую подпись. Тем же `#b91c1c` по тёмной кнопке `#334155` контраст выходит 1.6 : 1 —
   * подпись физически не читается, что и вылезло при первой сборке этой кнопки.
   */
  modalDangerText: string;
  modalBtnText: string;
  dmErr: string;
  retentionTimerBg: string;
  retentionTimerBorder: string;
  retentionTimerText: string;
};

const VAR: ChatListPalette = {
  screenRoot: '#0c1924',
  aliveDot: '#16a34a',
  scrim: 'rgba(12, 25, 36, 0.38)',
  chatFilterChip: 'rgba(15, 23, 42, 0.48)',
  chatFilterChipBorder: 'rgba(148, 163, 184, 0.26)',
  chatFilterChipActive: 'rgba(37, 99, 235, 0.18)',
  chatFilterChipActiveBorder: 'rgba(147, 197, 253, 0.65)',
  chatFilterChipText: 'rgba(226, 232, 240, 0.9)',
  chatFilterChipTextActive: '#dbeafe',
  chatSortBtnText: 'rgba(226, 232, 240, 0.9)',
  headerActionBtn: 'rgba(15, 23, 42, 0.52)',
  headerActionBtnBorder: 'rgba(148, 163, 184, 0.34)',
  headerIconTint: '#e2e8f0',
  h1: '#f8fafc',
  h2: '#94a3b8',
  smallBtn: 'rgba(30, 41, 59, 0.92)',
  smallBtnBorder: 'rgba(148, 163, 184, 0.3)',
  smallBtnText: '#e2e8f0',
  smallBtnAlert: 'rgba(251, 191, 36, 0.14)',
  smallBtnAlertBorder: 'rgba(251, 191, 36, 0.9)',
  channelIconStroke: '#e2e8f0',
  druzhinaPanel: 'rgba(15, 23, 42, 0.9)',
  druzhinaPanelBorder: 'rgba(148, 163, 184, 0.35)',
  druzhinaTitle: '#f8fafc',
  druzhinaAdd: '#93c5fd',
  druzhinaFilterChip: 'rgba(30, 41, 59, 0.68)',
  druzhinaFilterChipBorder: 'rgba(148, 163, 184, 0.35)',
  druzhinaFilterChipActive: 'rgba(22, 163, 74, 0.22)',
  druzhinaFilterChipActiveBorder: 'rgba(134, 239, 172, 0.8)',
  druzhinaFilterChipText: '#cbd5e1',
  druzhinaFilterChipTextActive: '#bbf7d0',
  druzhinaHint: '#94a3b8',
  druzhinaEmpty: '#64748b',
  druzhinaRow: '#0f172a',
  druzhinaRowBorder: '#334155',
  druzhinaName: '#e2e8f0',
  druzhinaActionIconTint: '#f8fafc',
  headerTitleShadow: true,
  itemSeparation: 'hairline',
  skeletonBlock: 'rgba(148, 163, 184, 0.22)',
  bar: 'rgba(12, 25, 36, 0.72)',
  barTint: 'dark',
  headerPattern: 'varangian',
  headerPatternTint: '#fcd9a0',
  headerPatternOpacity: 0.2,
  actionIcon: '#111827',
  actionIconBorder: '#334155',
  actionIconChat: '#1e293b',
  actionIconChatBorder: '#334155',
  actionIconCall: '#14532d',
  actionIconCallBorder: '#22c55e',
  actionIconVideo: '#1e3a8a',
  actionIconVideoBorder: '#60a5fa',
  requestTitle: '#94a3b8',
  requestRow: '#0f172a',
  requestRowBorder: '#334155',
  requestName: '#e2e8f0',
  requestApprove: '#86efac',
  requestReject: '#fca5a5',
  requestPending: '#93c5fd',
  contactRemoveSwipe: '#7f1d1d',
  contactRemoveSwipeBorder: '#ef4444',
  contactRemoveSwipeText: '#fee2e2',
  item: 'rgba(17, 24, 39, 0.92)',
  itemBorder: 'rgba(148, 163, 184, 0.28)',
  itemAccentDirect: 'rgba(147, 197, 253, 0.75)',
  itemAccentGroup: 'rgba(250, 204, 21, 0.85)',
  itemAccentChannel: 'rgba(74, 222, 128, 0.85)',
  itemAccentSaved: 'rgba(196, 181, 253, 0.85)',
  itemTitle: '#f8fafc',
  presenceDotOnline: '#22c55e',
  presenceDotOnlineBorder: 'rgba(240, 253, 244, 0.8)',
  presenceDotOffline: '#64748b',
  presenceDotOfflineBorder: 'rgba(226, 232, 240, 0.65)',
  groupOnlineBadge: 'rgba(34, 197, 94, 0.18)',
  groupOnlineBadgeBorder: 'rgba(134, 239, 172, 0.55)',
  groupOnlineBadgeText: '#bbf7d0',
  unreadBadge: '#ef4444',
  unreadBadgeBorder: 'rgba(255,255,255,0.4)',
  itemMeta: '#64748b',
  itemPreview: 'rgba(226, 232, 240, 0.62)',
  deleteBtn: 'rgba(30, 41, 59, 0.55)',
  deleteBtnBorder: 'rgba(148, 163, 184, 0.26)',
  notifyBtn: 'rgba(30, 41, 59, 0.55)',
  notifyBtnBorder: 'rgba(148, 163, 184, 0.26)',
  notifyMutedSlash: '#991b1b',
  empty: 'rgba(226, 232, 240, 0.85)',
  modalBg: 'rgba(0,0,0,0.55)',
  modalCard: '#1e293b',
  settingsSectionTitle: '#cbd5e1',
  settingsFieldLabel: '#94a3b8',
  settingsInlineHint: '#93c5fd',
  settingsSectionDivider: 'rgba(148, 163, 184, 0.25)',
  bossRegenBtn: '#334155',
  bossRegenBtnText: '#e2e8f0',
  bossRow: '#0f172a',
  bossRowBorder: '#334155',
  bossRowTitle: '#f8fafc',
  bossRowMeta: '#64748b',
  bossDelete: '#fca5a5',
  dmRow: '#0f172a',
  dmRowBorder: '#334155',
  dmRowName: '#f8fafc',
  dmRowHint: '#93c5fd',
  /**
   * Тёмная карточка модалки (`#1e293b`) требует ЗАЖЖЁННОГО серого: с `#64748b`, который тут
   * стоял, пустой список давал 3.07 : 1. Значения светлой и тёмной темы были фактически
   * перепутаны местами.
   */
  dmEmpty: '#94a3b8',
  channelDirRow: '#0f172a',
  channelDirRowBorder: '#334155',
  channelDirDescription: '#cbd5e1',
  channelDirMeta: '#64748b',
  gSelectedLabel: '#94a3b8',
  gSelectedRow: '#0f172a',
  gSelectedRowBorder: '#334155',
  gSelectedName: '#e2e8f0',
  gRemove: '#fca5a5',
  modalTitle: '#f8fafc',
  input: '#0f172a',
  inputBorder: '#334155',
  inputText: '#f8fafc',
  hint: '#94a3b8',
  modalBtn: '#334155',
  modalPrimary: '#2563eb',
  modalDanger: '#b91c1c',
  modalDangerText: '#fca5a5',
  modalBtnText: '#e2e8f0',
  dmErr: '#fca5a5',
  retentionTimerBg: 'rgba(251, 191, 36, 0.14)',
  retentionTimerBorder: 'rgba(252, 211, 77, 0.55)',
  retentionTimerText: 'rgba(254, 243, 199, 0.96)',
};

const NEUTRAL: ChatListPalette = {
  ...VAR,
  /**
   * Фон светлой темы — почти нейтральный, а не насыщенное поле.
   *
   * <p>Было `#bfdbfe` (blue-200) во весь экран. Белая карточка давала на нём 1.42 : 1 — она
   * едва отделялась, а сам фон при этом звучал громче содержимого: самый насыщенный цвет
   * экрана приходился на то, на что смотреть не нужно. Акцент теперь один — `#0369a1` на
   * кнопке и своём пузыре, а фон только подкрашен в синеву и уступает контенту.
   */
  screenRoot: '#e7edf5',
  aliveDot: '#0ea5e9',
  scrim: 'rgba(255, 255, 255, 0.48)',
  chatFilterChip: '#ffffff',
  chatFilterChipBorder: 'rgba(71, 85, 105, 0.35)',
  chatFilterChipActive: 'rgba(14, 165, 233, 0.26)',
  chatFilterChipActiveBorder: 'rgba(2, 132, 199, 0.52)',
  chatFilterChipText: 'rgba(15, 23, 42, 0.9)',
  chatFilterChipTextActive: '#0369a1',
  chatSortBtnText: 'rgba(15, 23, 42, 0.9)',
  headerActionBtn: '#ffffff',
  headerActionBtnBorder: 'rgba(71, 85, 105, 0.38)',
  headerIconTint: '#1e293b',
  h1: '#0f172a',
  h2: '#475569',
  smallBtn: '#ffffff',
  smallBtnBorder: 'rgba(71, 85, 105, 0.38)',
  smallBtnAlert: 'rgba(251, 191, 36, 0.24)',
  smallBtnAlertBorder: 'rgba(180, 83, 9, 0.9)',
  smallBtnText: '#1e293b',
  channelIconStroke: '#1e293b',
  druzhinaPanel: '#ffffff',
  druzhinaPanelBorder: 'rgba(71, 85, 105, 0.38)',
  druzhinaTitle: '#0f172a',
  /**
   * Акцент светлой темы — sky-700, а не sky-600. Тот же `#0284c7` стоял и подписью-ссылкой,
   * и заливкой под белым текстом; по белой панели он даёт 4.10 : 1 — ниже порога AA (4.5) для
   * обычного текста. `#0369a1` поднимает пару до 5.93 : 1 и уже совпадает с
   * `chatFilterChipTextActive` / `composerCaretColor`, которые до этого жили сами по себе.
   */
  druzhinaAdd: '#0369a1',
  druzhinaFilterChip: '#f8fafc',
  druzhinaFilterChipBorder: 'rgba(71, 85, 105, 0.35)',
  druzhinaFilterChipActive: 'rgba(14, 165, 233, 0.24)',
  druzhinaFilterChipActiveBorder: 'rgba(2, 132, 199, 0.52)',
  druzhinaFilterChipText: '#334155',
  druzhinaFilterChipTextActive: '#0369a1',
  druzhinaHint: '#475569',
  druzhinaEmpty: '#64748b',
  druzhinaRow: '#f8fafc',
  druzhinaRowBorder: '#cbd5e1',
  druzhinaName: '#0f172a',
  druzhinaActionIconTint: '#0f172a',
  headerTitleShadow: false,
  itemSeparation: 'shadow',
  skeletonBlock: 'rgba(15, 23, 42, 0.12)',
  bar: 'rgba(231, 237, 245, 0.72)',
  barTint: 'light',
  headerPattern: 'mail',
  headerPatternTint: '#0f172a',
  headerPatternOpacity: 0.1,
  actionIcon: '#f1f5f9',
  actionIconBorder: '#cbd5e1',
  actionIconChat: '#e2e8f0',
  actionIconChatBorder: '#94a3b8',
  actionIconCall: '#dcfce7',
  actionIconCallBorder: '#22c55e',
  actionIconVideo: '#dbeafe',
  actionIconVideoBorder: '#3b82f6',
  requestTitle: '#64748b',
  requestRow: '#f8fafc',
  requestRowBorder: '#cbd5e1',
  requestName: '#0f172a',
  requestApprove: '#15803d',
  requestReject: '#b91c1c',
  requestPending: '#0369a1',
  contactRemoveSwipe: '#fecaca',
  contactRemoveSwipeBorder: '#ef4444',
  contactRemoveSwipeText: '#7f1d1d',
  item: '#ffffff',
  itemBorder: 'rgba(71, 85, 105, 0.32)',
  itemAccentDirect: 'rgba(56, 189, 248, 0.85)',
  itemAccentGroup: 'rgba(251, 191, 36, 0.9)',
  itemAccentChannel: 'rgba(52, 211, 153, 0.88)',
  itemAccentSaved: 'rgba(139, 92, 246, 0.88)',
  itemTitle: '#0f172a',
  presenceDotOnline: '#16a34a',
  presenceDotOnlineBorder: 'rgba(220, 252, 231, 0.95)',
  presenceDotOffline: '#94a3b8',
  presenceDotOfflineBorder: 'rgba(71, 85, 105, 0.45)',
  groupOnlineBadge: 'rgba(34, 197, 94, 0.15)',
  groupOnlineBadgeBorder: 'rgba(22, 163, 74, 0.45)',
  groupOnlineBadgeText: '#166534',
  unreadBadge: '#e11d48',
  unreadBadgeBorder: 'rgba(255,255,255,0.55)',
  itemMeta: '#475569',
  itemPreview: 'rgba(30, 41, 59, 0.78)',
  deleteBtn: '#ffffff',
  deleteBtnBorder: 'rgba(71, 85, 105, 0.38)',
  notifyBtn: '#ffffff',
  notifyBtnBorder: 'rgba(71, 85, 105, 0.38)',
  notifyMutedSlash: '#b91c1c',
  empty: 'rgba(15, 23, 42, 0.78)',
  modalBg: 'rgba(15, 23, 42, 0.45)',
  modalCard: '#ffffff',
  settingsSectionTitle: '#475569',
  settingsFieldLabel: '#64748b',
  settingsInlineHint: '#0369a1',
  settingsSectionDivider: 'rgba(148, 163, 184, 0.35)',
  bossRegenBtn: '#e2e8f0',
  bossRegenBtnText: '#1e293b',
  bossRow: '#f8fafc',
  bossRowBorder: '#cbd5e1',
  bossRowTitle: '#0f172a',
  bossRowMeta: '#64748b',
  bossDelete: '#b91c1c',
  dmRow: '#f8fafc',
  dmRowBorder: '#cbd5e1',
  dmRowName: '#0f172a',
  dmRowHint: '#0369a1',
  /** Светлее нельзя: `#94a3b8` по белой карточке модалки — 2.56 : 1, подпись пропадает. */
  dmEmpty: '#64748b',
  channelDirRow: '#f8fafc',
  channelDirRowBorder: '#cbd5e1',
  channelDirDescription: '#475569',
  channelDirMeta: '#64748b',
  gSelectedLabel: '#64748b',
  gSelectedRow: '#f8fafc',
  gSelectedRowBorder: '#cbd5e1',
  gSelectedName: '#0f172a',
  gRemove: '#b91c1c',
  modalTitle: '#0f172a',
  input: '#f1f5f9',
  inputBorder: '#cbd5e1',
  inputText: '#0f172a',
  hint: '#64748b',
  modalBtn: '#e2e8f0',
  modalPrimary: '#0369a1',
  modalDanger: '#b91c1c',
  modalDangerText: '#b91c1c',
  modalBtnText: '#1e293b',
  dmErr: '#b91c1c',
  retentionTimerBg: 'rgba(251, 191, 36, 0.2)',
  retentionTimerBorder: 'rgba(217, 119, 6, 0.42)',
  retentionTimerText: '#9a3412',
};

const DARK: ChatListPalette = {
  ...VAR,
  screenRoot: '#09090b',
  bar: 'rgba(9, 9, 11, 0.72)',
  headerPattern: 'mail',
  headerPatternTint: '#e4e4e7',
  headerPatternOpacity: 0.13,
  aliveDot: '#22c55e',
  scrim: 'rgba(0, 0, 0, 0.52)',
  chatFilterChip: 'rgba(24, 24, 27, 0.88)',
  chatFilterChipBorder: 'rgba(63, 63, 70, 0.5)',
  chatFilterChipActive: 'rgba(59, 130, 246, 0.2)',
  chatFilterChipActiveBorder: 'rgba(96, 165, 250, 0.5)',
  chatFilterChipText: 'rgba(244, 244, 245, 0.9)',
  chatFilterChipTextActive: '#bfdbfe',
  chatSortBtnText: 'rgba(244, 244, 245, 0.9)',
  headerActionBtn: 'rgba(24, 24, 27, 0.9)',
  headerActionBtnBorder: 'rgba(63, 63, 70, 0.55)',
  headerIconTint: '#e4e4e7',
  h1: '#fafafa',
  h2: '#a1a1aa',
  smallBtn: 'rgba(39, 39, 42, 0.95)',
  smallBtnBorder: 'rgba(82, 82, 91, 0.55)',
  smallBtnAlert: 'rgba(251, 191, 36, 0.18)',
  smallBtnAlertBorder: 'rgba(251, 191, 36, 0.9)',
  smallBtnText: '#e4e4e7',
  channelIconStroke: '#e4e4e7',
  druzhinaPanel: 'rgba(24, 24, 27, 0.94)',
  druzhinaPanelBorder: 'rgba(63, 63, 70, 0.55)',
  druzhinaTitle: '#fafafa',
  druzhinaAdd: '#60a5fa',
  druzhinaFilterChip: 'rgba(39, 39, 42, 0.9)',
  druzhinaFilterChipBorder: 'rgba(82, 82, 91, 0.55)',
  druzhinaFilterChipActive: 'rgba(34, 197, 94, 0.2)',
  druzhinaFilterChipActiveBorder: 'rgba(74, 222, 128, 0.65)',
  druzhinaFilterChipText: '#d4d4d8',
  druzhinaFilterChipTextActive: '#bbf7d0',
  druzhinaHint: '#a1a1aa',
  druzhinaEmpty: '#71717a',
  druzhinaRow: '#18181b',
  druzhinaRowBorder: '#3f3f46',
  druzhinaName: '#f4f4f5',
  actionIcon: '#09090b',
  actionIconBorder: '#3f3f46',
  actionIconChat: '#27272a',
  actionIconChatBorder: '#52525b',
  actionIconCall: '#14532d',
  actionIconCallBorder: '#22c55e',
  actionIconVideo: '#1e3a8a',
  actionIconVideoBorder: '#60a5fa',
  requestTitle: '#a1a1aa',
  requestRow: '#18181b',
  requestRowBorder: '#3f3f46',
  requestName: '#e4e4e7',
  requestApprove: '#86efac',
  requestReject: '#fca5a5',
  requestPending: '#93c5fd',
  contactRemoveSwipe: '#7f1d1d',
  contactRemoveSwipeBorder: '#ef4444',
  contactRemoveSwipeText: '#fee2e2',
  item: 'rgba(24, 24, 27, 0.94)',
  itemBorder: 'rgba(63, 63, 70, 0.45)',
  itemAccentDirect: 'rgba(96, 165, 250, 0.85)',
  itemAccentGroup: 'rgba(250, 204, 21, 0.82)',
  itemAccentChannel: 'rgba(74, 222, 128, 0.82)',
  itemAccentSaved: 'rgba(167, 139, 250, 0.85)',
  itemTitle: '#fafafa',
  presenceDotOnline: '#22c55e',
  presenceDotOnlineBorder: 'rgba(240, 253, 244, 0.75)',
  presenceDotOffline: '#52525b',
  presenceDotOfflineBorder: 'rgba(212, 212, 216, 0.5)',
  groupOnlineBadge: 'rgba(34, 197, 94, 0.16)',
  groupOnlineBadgeBorder: 'rgba(134, 239, 172, 0.45)',
  groupOnlineBadgeText: '#bbf7d0',
  unreadBadge: '#ef4444',
  unreadBadgeBorder: 'rgba(255,255,255,0.35)',
  itemMeta: '#71717a',
  itemPreview: 'rgba(228, 228, 231, 0.62)',
  deleteBtn: 'rgba(39, 39, 42, 0.88)',
  deleteBtnBorder: 'rgba(82, 82, 91, 0.45)',
  notifyBtn: 'rgba(39, 39, 42, 0.88)',
  notifyBtnBorder: 'rgba(82, 82, 91, 0.45)',
  notifyMutedSlash: '#991b1b',
  empty: 'rgba(244, 244, 245, 0.88)',
  modalBg: 'rgba(0,0,0,0.72)',
  modalCard: '#27272a',
  settingsSectionTitle: '#e4e4e7',
  settingsFieldLabel: '#d4d4d8',
  settingsInlineHint: '#93c5fd',
  settingsSectionDivider: 'rgba(113, 113, 122, 0.65)',
  bossRegenBtn: '#3f3f46',
  bossRegenBtnText: '#f4f4f5',
  bossRow: '#18181b',
  bossRowBorder: '#52525b',
  bossRowTitle: '#fafafa',
  bossRowMeta: '#a1a1aa',
  bossDelete: '#fca5a5',
  dmRow: '#18181b',
  dmRowBorder: '#52525b',
  dmRowName: '#fafafa',
  dmRowHint: '#93c5fd',
  dmEmpty: '#a1a1aa',
  channelDirRow: '#18181b',
  channelDirRowBorder: '#52525b',
  channelDirDescription: '#e4e4e7',
  channelDirMeta: '#a1a1aa',
  gSelectedLabel: '#d4d4d8',
  gSelectedRow: '#18181b',
  gSelectedRowBorder: '#52525b',
  gSelectedName: '#f4f4f5',
  gRemove: '#fca5a5',
  modalTitle: '#fafafa',
  input: '#18181b',
  inputBorder: '#52525b',
  inputText: '#fafafa',
  hint: '#d4d4d8',
  modalBtn: '#3f3f46',
  modalPrimary: '#2563eb',
  modalDanger: '#b91c1c',
  modalDangerText: '#fca5a5',
  modalBtnText: '#f4f4f5',
  dmErr: '#fca5a5',
  retentionTimerBg: 'rgba(250, 204, 21, 0.12)',
  retentionTimerBorder: 'rgba(253, 224, 71, 0.45)',
  retentionTimerText: 'rgba(254, 249, 195, 0.95)',
};

/**
 * Sunlit daytime palette used by the `auto` theme during local daytime hours.
 * It is structurally a NEUTRAL clone warmed up with sun/amber accents so the
 * UI reads as "morning sky over a meadow" — high contrast, optimistic, and
 * still legible on direct sunlight.
 */
const SUN: ChatListPalette = {
  ...NEUTRAL,
  screenRoot: '#fde68a',
  bar: 'rgba(253, 230, 138, 0.72)',
  headerPatternTint: '#7c2d12',
  headerPatternOpacity: 0.12,
  scrim: 'rgba(254, 243, 199, 0.34)',
  chatFilterChip: '#fffbeb',
  chatFilterChipBorder: 'rgba(180, 83, 9, 0.32)',
  chatFilterChipActive: 'rgba(234, 88, 12, 0.2)',
  chatFilterChipActiveBorder: 'rgba(217, 119, 6, 0.6)',
  chatFilterChipText: '#7c2d12',
  chatFilterChipTextActive: '#9a3412',
  chatSortBtnText: '#7c2d12',
  headerActionBtn: '#fffbeb',
  headerActionBtnBorder: 'rgba(180, 83, 9, 0.34)',
  headerIconTint: '#7c2d12',
  h1: '#431407',
  h2: '#9a3412',
  smallBtn: '#fffbeb',
  smallBtnBorder: 'rgba(180, 83, 9, 0.34)',
  // Фон темы сам янтарный (#fde68a), поэтому подсветка уходит в оранжево-красный —
  // иначе заявка не отличается от обычной кнопки.
  smallBtnAlert: 'rgba(249, 115, 22, 0.26)',
  smallBtnAlertBorder: 'rgba(154, 52, 18, 0.92)',
  smallBtnText: '#7c2d12',
  channelIconStroke: '#7c2d12',
  druzhinaPanel: '#fffbeb',
  druzhinaPanelBorder: 'rgba(180, 83, 9, 0.34)',
  druzhinaTitle: '#431407',
  druzhinaAdd: '#b45309',
  druzhinaActionIconTint: '#7c2d12',
  headerTitleShadow: false,
  item: '#fffbeb',
  itemBorder: 'rgba(180, 83, 9, 0.3)',
  itemAccentDirect: 'rgba(245, 158, 11, 0.9)',
  itemAccentGroup: 'rgba(234, 88, 12, 0.85)',
  itemAccentChannel: 'rgba(132, 204, 22, 0.85)',
  itemAccentSaved: 'rgba(168, 85, 247, 0.85)',
  itemTitle: '#431407',
  itemMeta: '#92400e',
  itemPreview: 'rgba(120, 53, 15, 0.82)',
  deleteBtn: '#fffbeb',
  deleteBtnBorder: 'rgba(180, 83, 9, 0.34)',
  notifyBtn: '#fffbeb',
  notifyBtnBorder: 'rgba(180, 83, 9, 0.34)',
  notifyMutedSlash: '#9a3412',
  empty: 'rgba(67, 20, 7, 0.78)',
};

/**
 * Cosmic night palette used by the `auto` theme during local night hours.
 * Structurally a VAR/DARK hybrid — even deeper background than `dark` so the
 * starfield wallpaper has room to glow, with cooler indigo accents that line
 * up with the night-sky imagery rendered behind the list.
 */
const COSMOS: ChatListPalette = {
  ...VAR,
  screenRoot: '#020617',
  bar: 'rgba(2, 6, 23, 0.72)',
  headerPattern: 'mail',
  headerPatternTint: '#c7d2fe',
  headerPatternOpacity: 0.14,
  scrim: 'rgba(2, 6, 23, 0.55)',
  chatFilterChip: 'rgba(15, 23, 42, 0.7)',
  chatFilterChipBorder: 'rgba(129, 140, 248, 0.32)',
  chatFilterChipActive: 'rgba(129, 140, 248, 0.28)',
  chatFilterChipActiveBorder: 'rgba(199, 210, 254, 0.7)',
  chatFilterChipText: 'rgba(226, 232, 240, 0.88)',
  chatFilterChipTextActive: '#e0e7ff',
  headerActionBtn: 'rgba(15, 23, 42, 0.75)',
  headerActionBtnBorder: 'rgba(129, 140, 248, 0.4)',
  headerIconTint: '#e0e7ff',
  h1: '#f8fafc',
  h2: '#a5b4fc',
  smallBtn: 'rgba(15, 23, 42, 0.92)',
  smallBtnBorder: 'rgba(129, 140, 248, 0.4)',
  smallBtnAlert: 'rgba(251, 191, 36, 0.16)',
  smallBtnAlertBorder: 'rgba(252, 211, 77, 0.92)',
  smallBtnText: '#e0e7ff',
  channelIconStroke: '#e0e7ff',
  druzhinaPanel: 'rgba(2, 6, 23, 0.92)',
  druzhinaPanelBorder: 'rgba(129, 140, 248, 0.42)',
  druzhinaTitle: '#f8fafc',
  druzhinaAdd: '#a5b4fc',
  druzhinaActionIconTint: '#f8fafc',
  headerTitleShadow: true,
  item: 'rgba(15, 23, 42, 0.92)',
  itemBorder: 'rgba(129, 140, 248, 0.3)',
  itemAccentDirect: 'rgba(165, 180, 252, 0.85)',
  itemAccentGroup: 'rgba(250, 204, 21, 0.85)',
  itemAccentChannel: 'rgba(94, 234, 212, 0.85)',
  itemAccentSaved: 'rgba(192, 132, 252, 0.85)',
  itemTitle: '#f8fafc',
  itemMeta: '#94a3b8',
  itemPreview: 'rgba(226, 232, 240, 0.7)',
  deleteBtn: 'rgba(15, 23, 42, 0.65)',
  deleteBtnBorder: 'rgba(129, 140, 248, 0.32)',
  notifyBtn: 'rgba(15, 23, 42, 0.65)',
  notifyBtnBorder: 'rgba(129, 140, 248, 0.32)',
  notifyMutedSlash: '#fda4af',
  empty: 'rgba(226, 232, 240, 0.88)',
};

export function chatListPalette(id: VisualThemeId): ChatListPalette {
  if (id === 'neutral') return NEUTRAL;
  if (id === 'dark') return DARK;
  if (id === 'sun') return SUN;
  if (id === 'cosmos') return COSMOS;
  // `auto` is never expected here (the provider resolves it before lookup);
  // varangian remains the historical fallback for any unrecognised value.
  return VAR;
}
